import React, {useState} from 'react';
import PortalMap from "@Portals/components/PortalMap";
import datasetParser from "@Dataset/datasetParser";
import {getFirstLetter} from "@String/getFirstLetter";
import Notification from "@Features/notification/components/Notification";
import {useTranslation} from "@Translation/hooks/useTranslation";
import Translation from "@Translation/components/Translation";
import TagFilter from "@Pages/countryPage/components/TagFilter";
import AlphabetSelection from "@Pages/countryPage/components/AlphabetSelection";
import LetterSectionedItems from "@Pages/countryPage/components/LetterSectionedItems";
import useIncludedFirstLetters from "@Pages/countryPage/hooks/useIncludedFirstLetters";
import StationTagFilter from "@Pages/countryPage/features/countryStations/components/StationTagFilter";
import StationFeaturesType from "@Pages/countryPage/features/countryStations/types/StationFeatures.type";
import Portal from "@Portals/components/Portal";
import GenericPopover from "@Popover/components/GenericPopover";
import IconInfoCircle from "@Icons/filledOutline/IconInfoCircle";

function CountryStations() {
    const {country, stations: parsedStations, stationFeatureCount} = datasetParser({
        className: "react--portal__bus-stations-in-country",
        index: 0,
    });
    const [stations] = useState(parsedStations)
    const [filteredStations, setFilteredStations] = useState(stations)
    const [activeLetter, setActiveLetter] = useState(null)
    const [activeTag, setActiveTag] = useState("all")
    const includedFirstLetters = useIncludedFirstLetters(stations, 'name')
    const filterTagLabel = useTranslation("country_page.country_bus_stations.filter_by_category")
    const filterInCountryLabel = useTranslation("country_page.country_bus_stations.filter_stations_in_country", {country})
    const overlayContent = useTranslation("country_page.country_bus_stations.info_button")

    const filterByLetter = (letter) => () => {
        setActiveTag("all")
        if (letter !== activeLetter) {
            setActiveLetter(letter)
            setFilteredStations(stations.filter(({name}) => (letter === getFirstLetter(name))))
        } else {
            setFilteredStations(stations)
            setActiveLetter(null)
        }
    }

    const filterByFeature = (feature) => {
        setActiveTag(feature)
        setActiveLetter(null)
        if (feature !== 'all') {
            const filterStations = stations.filter((station) => (station[feature]))
            setFilteredStations(filterStations)
        } else {
            setFilteredStations(stations)
        }
    }

    return (
        <>
            <Portal id="review-info">
                 <GenericPopover
                    trigger={<IconInfoCircle backgroundFill="#000000" width="22" height="22"/>}
                    overlay={overlayContent}
                    id="review-info-button"
                 />
            </Portal>
            <PortalMap className="react--portal__bus-stations-in-country">
                <>
                    <div className="gb--card gb--card-default gb--card-filters">
                        <TagFilter title={filterTagLabel}>
                            <StationTagFilter
                                onClick={filterByFeature}
                                activeTag={activeTag}
                                stationFeatureCount={stationFeatureCount as StationFeaturesType}
                            />
                        </TagFilter>

                        <h2>{filterInCountryLabel}</h2>
                        <AlphabetSelection
                            activeLetter={activeLetter}
                            includedLetters={includedFirstLetters}
                            filterByLetter={filterByLetter}
                        />
                    </div>

                    {filteredStations.length !== stations.length && (
                        <Notification notificationType="green">
                            <Translation
                                translationKey="country_page.country_bus_stations.showing"
                                translationVariables={{
                                    "%numberOfFilteredStations%": filteredStations.length,
                                    "%numberOfTotalStations%": stations.length,
                                    "%country%": country,
                                }}
                                exact
                            />
                        </Notification>
                    )}

                    <LetterSectionedItems
                        arrayKey="name"
                        items={filteredStations ?? stations}
                        type="stations"
                    />
                </>
            </PortalMap>
        </>
    );
}

export default CountryStations;